export const inventoryPageData = {
  stats: [
    { value: "40%", label: "Inventory Cost Reduction" },
    { value: "99.9%", label: "Stock Accuracy" },
    { value: "0", label: "Manual Stockouts" },
    { value: "Real-Time", label: "Visibility Across Locations" },
  ],
  statsAccent: "from-blue-600 to-indigo-700",

  challengeTitle: "Inventory Mismanagement Is Silently Costing You Millions",
  challengeSubtitle:
    "Stockouts, overstock, and manual tracking aren't just operational problems — they're revenue-destroying profit killers.",
  challenges: [
    {
      icon: "📉",
      title: "Stockouts of Fast-Moving Items",
      description:
        "Running out of your best-selling products means lost sales, disappointed customers, and revenue that goes to competitors.",
    },
    {
      icon: "📦",
      title: "Dead Stock Draining Capital",
      description:
        "Excess inventory of slow-moving items ties up working capital, takes up warehouse space, and often ends in write-offs.",
    },
    {
      icon: "⏰",
      title: "Hours Wasted on Manual Counts",
      description:
        "Periodic stock counts are time-consuming, disruptive, and almost always inaccurate — causing downstream errors.",
    },
    {
      icon: "👁️",
      title: "No Multi-Location Visibility",
      description:
        "Without real-time visibility across warehouses and stores, stock redistribution decisions are blind guesses.",
    },
    {
      icon: "🏷️",
      title: "Pricing Errors and Inconsistencies",
      description:
        "Manual price updates across SKUs and locations lead to errors that erode margins and create customer complaints.",
    },
    {
      icon: "🔄",
      title: "Reactive, Not Predictive Replenishment",
      description:
        "Reordering based on today's levels — not forecasted demand — means you're always playing catch-up.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-blue-50 text-blue-700 border-blue-100",
    solutionIconBg: "bg-blue-50",
    solutionIconColor: "text-blue-600",
    solutionAccent: "from-blue-600 to-indigo-600",
  },

  solutionTitle: "Real-Time Inventory Intelligence Powered by AI",
  solutionSubtitle:
    "Complete visibility, AI demand forecasting, and automated replenishment — so you always have the right stock, in the right place, at the right time.",
  solutions: [
    {
      icon: "📡",
      title: "Real-Time Inventory Tracking",
      description:
        "Stock levels update instantly as goods move in and out — across every location, warehouse, and sales channel.",
    },
    {
      icon: "🧠",
      title: "AI Demand Forecasting",
      description:
        "Predict demand for every SKU using historical sales, seasonality patterns, and market signals weeks in advance.",
    },
    {
      icon: "🔔",
      title: "Automated Reorder Triggers",
      description:
        "Smart reorder points trigger purchase orders automatically — preventing stockouts before they happen.",
    },
    {
      icon: "🏪",
      title: "Multi-Location Management",
      description:
        "Unified visibility and control across unlimited warehouse and store locations from a single dashboard.",
    },
    {
      icon: "🏷️",
      title: "Automated Product Publishing",
      description:
        "Products auto-publish when in stock and auto-unpublish when depleted — preventing overselling on all channels.",
    },
    {
      icon: "📦",
      title: "Bundle & Promo Management",
      description:
        "Create and manage product bundles, kits, and promotional offers with real-time component inventory tracking.",
    },
    {
      icon: "🔄",
      title: "Inter-Location Stock Transfers",
      description:
        "AI recommends optimal stock transfers between locations to balance inventory and prevent localised stockouts.",
    },
    {
      icon: "📊",
      title: "Inventory Analytics",
      description:
        "Turnover rates, slow-movers, dead stock flags, and margin analytics — everything you need to optimise your catalogue.",
    },
    {
      icon: "🔌",
      title: "ERP & POS Integration",
      description:
        "Sync inventory data with your ERP, POS, and e-commerce platforms in real time for a unified data view.",
    },
  ],

  howItWorksTitle: "Intelligent Inventory Management in 4 Steps",
  howItWorksSteps: [
    {
      icon: "🔗",
      title: "Connect Your Systems",
      description: "Integrate EkkBaz AI with your ERP, POS, and supplier systems via pre-built connectors.",
    },
    {
      icon: "📊",
      title: "AI Learns Your Patterns",
      description: "The AI engine analyses your historical sales, seasonality, and supplier lead times.",
    },
    {
      icon: "🤖",
      title: "Automation Takes Over",
      description: "Reorder triggers, stock transfers, and product publishing all run automatically.",
    },
    {
      icon: "📈",
      title: "Costs Drop, Sales Rise",
      description: "Monitor inventory cost reduction, stockout rate, and working capital improvement in real time.",
    },
  ],

  outcomeTitle: "The Inventory Outcomes That Transform Your Business",
  outcomeSubtitle: "Measurable results from businesses using EkkBaz AI Inventory Management.",
  outcomes: [
    {
      value: "40%",
      metric: "Lower Inventory Costs",
      description:
        "AI demand forecasting and automated replenishment eliminate overstock and dead stock — reducing total inventory holding costs by 40%.",
    },
    {
      value: "99.9%",
      metric: "Stock Accuracy",
      description:
        "Real-time tracking and automated reconciliation achieve near-perfect inventory accuracy — no more manual counting.",
    },
    {
      value: "Zero",
      metric: "Preventable Stockouts",
      description:
        "Predictive reorder triggers eliminate stockouts of fast-moving items — so you never miss a sale due to empty shelves.",
    },
  ],
  outcomeImage: "/assets/images/products/inventory2.png",

  ctaTitle: "Eliminate Stockouts, Cut Inventory Costs by 40%",
  ctaSubtitle:
    "Book a free consultation and see how EkkBaz AI can transform your inventory management — delivering results in your first quarter.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const inventoryData = [
  {
    id: 1,
    extraLabel: "The Challenge",
    userType: "Stockouts, Overstock, and Manual Inventory Management Are Costing You",
    description:
      "Businesses relying on manual inventory tracking face constant stockouts of fast-moving items, excess capital tied up in slow-moving stock, and pricing errors that damage margins. Without real-time visibility across locations, inventory management is always reactive — never predictive.",
    banner: "/assets/images/products/inventory1.png",
  },
  {
    id: 2,
    extraLabel: "Our Solution",
    userType: "Real-Time Inventory Control with AI-Powered Intelligence",
    description:
      "EkkBaz AI provides complete real-time inventory visibility across all locations and warehouses. As stock comes in from suppliers, levels update instantly with accurate pricing. Create and manage products, bundles, and promotional offers effortlessly. Automatic publish/unpublish based on availability ensures customers always see accurate stock — eliminating order failures and customer disappointment.",
    banner: "/assets/images/products/inventory2.png",
  },
  {
    id: 3,
    extraLabel: "The Outcome",
    userType: "Reduce Inventory Costs 40% with AI Demand Forecasting and Automation",
    description:
      "EkkBaz AI's demand forecasting engine analyzes sales patterns, seasonal trends, and supplier lead times to automatically trigger reorders at precisely the right moment — preventing stockouts while reducing excess inventory by 40%. Real-time tracking and AI restocking recommendations give operations teams the intelligence to make confident decisions at every level.",
    banner: "/assets/images/products/inventory3.png",
  },
];

export const inventoryFAQs = [
  {
    question: "How does EkkBaz AI prevent stockouts?",
    answer:
      "EkkBaz AI's demand forecasting engine analyzes historical sales data, seasonal patterns, and current sell-through rates to predict when items will run out — and automatically triggers reorder alerts or purchase orders before stockout thresholds are reached.",
  },
  {
    question: "Can EkkBaz AI manage inventory across multiple locations?",
    answer:
      "Yes. EkkBaz AI provides real-time inventory visibility across unlimited warehouses and store locations, with centralized control and location-level granularity — enabling optimal stock redistribution between locations to prevent stockouts and reduce overstock.",
  },
  {
    question: "How does EkkBaz AI handle product catalog management?",
    answer:
      "EkkBaz AI provides an intuitive product catalog management system where you can create individual products, bundles, and promotional offers, set dynamic pricing rules, and automatically publish or unpublish products based on real-time availability.",
  },
];
